import { Dispatch, PropsWithChildren, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { signInEmailSchema, SignInEmailType } from '@/schemes/sign-in-email-schema'
import { AxiosError, AxiosResponse } from 'axios'
import { EmailFormAddressStep } from '../email-form-address-step'
import { EmailFormOtpStep } from '../email-form-otp-step'
import { useToast } from '@/store/useToast'
import { ErrorNotificationIcon } from '@/assets/icons'
import { useTranslation } from 'react-i18next'
import { ButtonTextColor } from '@/ui-kit/button'
import clsx from 'clsx'
import styles from './email-form.module.scss'
import { VerifyOtpResponse } from '@/api/auth'

interface Props {
    emailFormClass?: string
    useGradient?: boolean
    emailHeader?: ReactNode
    nextBtnStyles?: string
    disabledNextBtnStyles?: string
    showSkipBtn?: boolean
    skipBtnStyles?: string
    skipBtnTextColor?: ButtonTextColor
    nextBtnTextColor?: ButtonTextColor
    emailTitleKey?: string
    emailSubtitleKey?: string
    requestOTP: (email: string) => Promise<AxiosResponse<{ countdown: number }>>
    verifyOTP: (email: string, code: string) => Promise<AxiosResponse>
    onLogin: (data: VerifyOtpResponse) => void
    onSkip?: () => Promise<void>
    onError?: (
        err: AxiosError<any>,
        setError: Dispatch<SetStateAction<string | null>>,
        showErrorToast: () => void
    ) => void
}

export const EmailForm: React.FC<Props> = ({
    emailFormClass,
    emailHeader,
    nextBtnStyles,
    disabledNextBtnStyles,
    showSkipBtn,
    skipBtnStyles,
    skipBtnTextColor,
    nextBtnTextColor,
    useGradient = true,
    emailTitleKey,
    emailSubtitleKey,
    requestOTP,
    verifyOTP,
    onSkip,
    onLogin,
}) => {
    const CODE_SEND_TIMEOUT_ERROR = 'code_send_timeout'
    const EMAIL_FIELD = 'email'
    const EMAIL_STEP = 1
    const OTP_STEP = 2
    const DEFAULT_RESEND_CODE_TIMEOUT = 60
    
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [step, setStep] = useState(EMAIL_STEP)
    const [resendCodeDelay, setResendCodeDelay] = useState(DEFAULT_RESEND_CODE_TIMEOUT)
    const [secondsBeforeOTPRequest, setSecondsBeforeOTPRequest] = useState<number | null>(null)
    const [otpInterval, setOtpInterval] = useState<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (otpInterval) clearTimeout(otpInterval)
        setOtpInterval(null)
    }, [])

    const initialValues = useMemo(() => {
        return { [EMAIL_FIELD]: '' }
    }, [])

    const signInEmailMethods = useForm<SignInEmailType>({
        resolver: zodResolver(signInEmailSchema),
        defaultValues: initialValues
    })

    const updateTimer = () => {
        const interval = setTimeout(
            () => {
                clearTimeout(interval)
                setSecondsBeforeOTPRequest(seconds => {
                    let val = resendCodeDelay
                    if (seconds) val = --seconds
                    if (val <= 0) {
                        if (interval) {
                            clearTimeout(interval)
                            setOtpInterval(null)
                        }
                        return null
                    }
                    setOtpInterval(updateTimer())
                    return val
                })
            },
            1000
        )
        return interval
    }
    
    const startOtpResendCodeTimer = () =>
        setOtpInterval(() => {
            setSecondsBeforeOTPRequest(resendCodeDelay)
            return updateTimer()
        })

    const onReturnToEmailInput = () => {
        setError(null)
        setStep(EMAIL_STEP)
    }

    const showToast = useToast(state => state.show)

    const { t } = useTranslation()

    const showErrorToast = () => {
        const CONTENT_PADDING_BOTTOM = 48
        const BTN_HEIGHT = 64
        const GAP = 16
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: t('error_try_again'),
            bottom: CONTENT_PADDING_BOTTOM + BTN_HEIGHT + GAP,
        })
    }

    const sendCode = async () => {
        setError(null)
        try {
            setIsLoading(true)
            const res =
                await requestOTP(signInEmailMethods.getValues(EMAIL_FIELD))
            setResendCodeDelay(res.data.countdown)
            if (otpInterval) clearTimeout(otpInterval)
            startOtpResendCodeTimer()
            setStep(OTP_STEP)
        } catch (error) {
            if (error instanceof AxiosError) {
                if (
                    error.response?.status === 422 &&
                    error.response?.data.error
                ) {
                    // code already was requested to current email and otp timeout not expired
                    if (error.response?.data.code === CODE_SEND_TIMEOUT_ERROR) {
                        if (otpInterval) {
                            clearTimeout(otpInterval)
                            setOtpInterval(null)
                        }
                        setResendCodeDelay(error.response?.data.countdown)
                        startOtpResendCodeTimer()
                        setStep(OTP_STEP)
                    }
                    setError(error.response?.data.error)
                } else showErrorToast()
            }
        } finally {
            setIsLoading(false)
        }
    }

    const resendCode = async () => {
        setError(null)
        try {
            setIsLoading(true)
            const res =
                await requestOTP(signInEmailMethods.getValues(EMAIL_FIELD))
            setResendCodeDelay(res.data.countdown)
            startOtpResendCodeTimer()
        } catch (error) {
            if (error instanceof AxiosError) {
                if (
                    error.response?.status === 422 &&
                    error.response?.data.error
                ) {
                    // force show common error for otp form (email or otp errors) without redirect to email form
                    setError(error.response?.data.error)
                } else showErrorToast()
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleSkip = async () => {
        try {
            setIsLoading(true)
            await onSkip?.()
        } finally {
            setIsLoading(false)
        }
    }
    
    switch (step) {
        case EMAIL_STEP:
            return (
                <EmailFormAddressStep
                    className={clsx(emailFormClass, useGradient && styles.withGradient)}
                    signInEmailMethods={signInEmailMethods}
                    isLoading={isLoading}
                    error={error}
                    useGradient={useGradient}
                    setError={setError}
                    sendCode={sendCode}
                    nextBtnStyles={nextBtnStyles}
                    disabledNextBtnStyles={disabledNextBtnStyles}
                    showSkipBtn={showSkipBtn}
                    skipBtnStyles={skipBtnStyles}
                    skipBtnTextColor={skipBtnTextColor}
                    nextBtnTextColor={nextBtnTextColor}
                    titleKey={emailTitleKey}
                    subtitleKey={emailSubtitleKey}
                    emailHeader={emailHeader}
                    onSkip={handleSkip} />
            )
        case OTP_STEP:
            return (
                <EmailFormOtpStep
                    useGradient={useGradient}
                    className={clsx(emailFormClass, useGradient && styles.withGradient)}
                    nextBtnStyles={nextBtnStyles}
                    disabledNextBtnStyles={disabledNextBtnStyles}
                    secondsBeforeOTPRequest={secondsBeforeOTPRequest}
                    email={signInEmailMethods.getValues(EMAIL_FIELD)}
                    error={error}
                    setError={setError}
                    returnToEmailInput={onReturnToEmailInput}
                    showErrorToast={showErrorToast}
                    nextBtnTextColor={nextBtnTextColor}
                    resendCode={resendCode}
                    verifyOTP={verifyOTP}
                    onLogin={onLogin} />
            )
        default:
            return <>Error 404</>
    }
}
